<template>
    <div :class="namespaceClass()">
        <div :class="namespaceClass('title')">{{ this.getUILabel("home_page_title") }}</div>
        <div :class="namespaceClass('actions')">
            <mds-button-group :class="namespaceClass('__tabs')" :content="fileTabContent" @mds-button-group-item-active="handleActiveTabChanged">
            </mds-button-group>
            <mds-button
                id="db-filter"
                variation="icon-only"
                icon="funnel"
                :class="namespaceClass('filter') + filterActive"
                :disabled="activeTabId === 'reports'"
                @mouseover="filterHover = true"
                @mouseleave="filterHover = false"
                @click="filterShowMenu = !filterShowMenu"
            >
            </mds-button>
            <mds-popover class="filter-menu" triggered-by="db-filter" v-model="filterShowMenu" :position="['bottom-center']">
                <mds-form>
                    <mds-fieldset variation="radio-group" legend="View">
                        <hr />
                        <mds-radio-button name="all" v-model="filter" value="all" checked>{{ getUILabel("ARCHIVE_FILTER_ALL") }}</mds-radio-button>
                        <mds-radio-button name="firm-saved" v-model="filter" value="firm-saved">{{
                            getUILabel("ARCHIVE_FILTER_FIRM_SAVED_ONLY")
                        }}</mds-radio-button>
                    </mds-fieldset>
                </mds-form>
            </mds-popover>
            <mds-button
                variation="icon-only"
                icon="download"
                text="Download"
                :class="namespaceClass('download')"
                :disabled="activeTabId !== 'reports'"
                @click="handleDownload"
            >
            </mds-button>
            <mds-button
                variation="icon-only"
                icon="trash"
                text="Delete"
                :class="namespaceClass('delete')"
                :disabled="deleteButtonDissabled"
                @click="handleDelete"
            >
            </mds-button>
            <mds-button
                id="db-duplicate"
                variation="icon-only"
                text="Duplicate"
                icon="caret-circle-down"
                :class="namespaceClass('duplicate') + duplicateBlue"
                :disabled="duplicateDisabled"
                @mouseover="duplicateHover = true"
                @mouseleave="duplicateHover = false"
                @click="duplicateShowMenu = !duplicateShowMenu"
            >
            </mds-button>
            <mds-popover class="duplicate-menu" triggered-by="db-duplicate" v-model="duplicateShowMenu" :position="['bottom-center']">
                <mds-list-group>
                    <mds-list-group-item @mds-list-item-clicked="handleShowDuplicate" :text="getUILabel('HOME_CARD_MENU_DUPLICATE')"></mds-list-group-item>
                </mds-list-group>
            </mds-popover>
            <mds-search-field
                variation="secondary"
                :placeholder="searchPlaceHolder()"
                :class="namespaceClass('searchBy')"
                @input="handleSearch"
                v-model="searchByName"
            ></mds-search-field>
        </div>
        <Hypotheticals
            ref="hypotheticals"
            v-if="activeTabId === 'hypotheticals'"
            :filter="filter"
            @delete-dissabled="deleteButtonDissabled = $event"
            @duplicate-dissabled="duplicateButtonDisabled = $event"
            :mwc-id="getMwcIdForChild('hypotheticals')"
        />
        <Reports ref="reports" v-else @delete-dissabled="deleteButtonDissabled = $event" :mwc-id="getMwcIdForChild('reports')" />
        <save-dialog
            v-if="showSaveDialog"
            :mwc-id="getMwcIdForChild('saveDialog')"
            :duplicateHypoName="duplicateHypoName"
            :showDontSave="false"
            :show="showSaveDialog"
            @save-event="handleSaveDialog"
        ></save-dialog>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import { MdsButton } from "@mds/button";
import MdsButtonGroup from "@mds/button-group";
import MdsSearchField from "@mds/search-field";
import MdsPopover from "@mds/popover";
import { MdsListGroup, MdsListGroupItem } from "@mds/list-group";
import MdsForm from "@mds/form";
import MdsFieldset from "@mds/fieldset";
import MdsRadioButton from "@mds/radio-button";
import Hypotheticals from "./Hypotheticals";
import Reports from "./Reports";
import SaveDialog from "../NewWorkflow/SaveDialog";
import { showNotification } from "@/util.js";

export default {
    name: "dashboard-view",
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        Hypotheticals,
        MdsButton,
        MdsButtonGroup,
        MdsPopover,
        MdsListGroup,
        MdsListGroupItem,
        MdsForm,
        MdsFieldset,
        MdsRadioButton,
        MdsSearchField,
        Reports,
        SaveDialog
    },
    data() {
        return {
            fileTabContent: [],
            activeTabId: "reports",
            deleteButtonDissabled: true,
            duplicateButtonDisabled: true,
            defaultConfig: {
                labels: this.$store.state.labels.labels
            },
            searchByName: "",
            duplicateShowMenu: false,
            duplicateHover: false,
            showSaveDialog: false,
            hypoDataForDuplicate: [],
            filter: "all",
            filterShowMenu: false,
            filterHover: false
        };
    },
    computed: {
        isReportListActive() {
            return this.activeTabId === "reports";
        },
        duplicateBlue() {
            return this.duplicateShowMenu || this.duplicateHover ? " icon-blue" : "";
        },
        duplicateDisabled() {
            const totalCount = this.$store.getters["notifications/getHypoCount"];
            const actualFileLimit = this.$store.getters["config/getSavedHyposLimit"];
            return totalCount >= actualFileLimit || this.activeTabId === "reports" || this.duplicateButtonDisabled;
        },
        duplicateHypoName() {
            const data = this.hypoDataForDuplicate;
            return data && data.hypoInput ? `${data.hypoInput.hypoName} Copy` : "";
        },
        filterSelected() {
            return this.filter !== "all";
        },
        filterActive() {
            return this.filterShowMenu || this.filterHover || this.filterSelected ? " icon-blue" : "";
        }
    },
    watch: {
        filter: function() {
            this.filterShowMenu = false;
        }
    },
    mounted() {
        this.fileTabContent = [
            {
                id: "hypotheticals",
                text: this.getUILabel("archive_tabs_hypos"),
                active: false
            },
            {
                id: "reports",
                text: this.getUILabel("archive_tabs_reports"),
                active: true
            }
        ];
    },
    methods: {
        searchPlaceHolder() {
            const labelId = this.isReportListActive ? "archive_reports_actions_search_placeholder" : "archive_hypos_actions_search_placeholder";
            return this.getUILabel(labelId);
        },
        handleActiveTabChanged(e) {
            this.activeTabId = e.target.id;
            this.fileTabContent.forEach(item => {
                if (item.id === e.target.id) {
                    item.active = true;
                } else {
                    item.active = false;
                }
            });
            this.deleteButtonDissabled = true;
            this.filter = "all";
        },
        handleSearch() {
            this.$refs[this.activeTabId].handleSearch(this.searchByName);
        },
        handleDelete() {
            this.$refs[this.activeTabId].handleDelete();
        },
        handleDownload() {
            this.$refs.reports.handleDownload();
        },
        async handleShowDuplicate() {
            this.hypoDataForDuplicate = await this.$refs["hypotheticals"].sendHypoDataForDuplicate();
            if (this.hypoDataForDuplicate) {
                this.showSaveDialog = true;
            } else {
                showNotification(this.$store, "Fail to fetch data for selected file", "error");
            }
        },
        handleSaveDialog(event) {
            // Cancel button clicked
            if (!event) {
                this.showSaveDialog = false;
            }
            // Save button clicked
            else if (!event.hasError) {
                this.$refs["hypotheticals"].tableDataLoading = true;
                const postData = { ...this.hypoDataForDuplicate.hypoInput, hypoName: event.newHypoName };
                delete postData["hypoId"];
                this.$store
                    .dispatch("putOrPostHypo", {
                        fetch: this.$fetch,
                        hypoInput: postData
                    })
                    .then(() => {
                        // Fetch table data and turn off table loading
                        this.filter = "all";
                        this.$refs["hypotheticals"].initTable();
                        showNotification(this.$store, "Successfully duplicated the file");
                        this.duplicateButtonDisabled = true;
                    })
                    .catch(err => {
                        console.log(err);
                        this.$refs["hypotheticals"].tableDataLoading = false;
                        showNotification(this.$store, "Fail to duplicate the file", "error");
                    });
            }
        }
    }
};
</script>
<style lang="scss">
.mds-search-field__clear-button___hypo {
    position: absolute !important;
    right: 12px !important;
    top: 50% !important;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%) !important;
}
</style>
<style lang="scss" scoped>
@import "~@mds/typography";

.filter-menu {
    hr {
        height: 3px;
        background-color: $mds-background-color-black;
        margin: 2px 0 12px 0;
    }
}

// Change icon color
::v-deep .icon-blue {
    .mds-button__icon___hypo.mds-button__icon___hypo.mds-button__icon___hypo {
        stroke: $mds-interactive-color-primary-default;
    }
}

.dashboard-view {
    margin-top: $mds-space-6-x;
    &-title {
        @include mds-level-5-heading($bold: false);
        color: $mds-text-color-primary;
        margin-bottom: $mds-space-3-x;
    }
    &__tabs {
        margin-right: 13px;
    }
    &-actions {
        display: flex;
        margin: $mds-space-2-x 0;
        height: 49px;
        align-items: center;
        margin-bottom: $mds-space-2-x;
        border-top: $mds-border-separator;
        border-bottom: $mds-border-separator;
    }

    &-filter:focus {
        box-shadow: none;
    }

    &-download,
    &-delete,
    &-duplicate {
        margin-left: $mds-space-1-x;
    }

    &-duplicate:focus {
        box-shadow: none;
    }

    &-searchBy {
        margin-bottom: 0px;
        margin-left: $mds-space-1-x;
        width: 240px;
    }
}
</style>
